import React from 'react';
import Table from '../../components/common/Table';
import { ReconTransactionDTO } from '../../services/apiClient';
import { convertToUserTime } from '../../utils/dateUtils/convertToUserTime';
import { renderBooleanBadge } from '../../utils/displayUtils';
import createReconTransactionActions from '../actionMenus/ReconTransactionActions';

interface ReconTransactionTableProps {
  data: ReconTransactionDTO[];
  handleViewMember: (idPassportNumber: string) => void;
  handleEditMember: (memberId: string) => void;
  handleCreateInvoice: (transaction: ReconTransactionDTO) => void;
  handleViewInvoiceHistory?: (transaction: ReconTransactionDTO) => void;
  handleViewMemberBatches?: (transaction: ReconTransactionDTO) => void;
  handleSetIsUnallocated: (transactionId: string, isUnallocated: boolean) => void;
  handleSetComponentId?: (transactionId: string, componentId: string) => void;
  handleAssignMember?: (transaction: ReconTransactionDTO) => void;
  handleIgnoreTransaction?: (transactionId: string) => void;
  showConfirmComponentActions?: boolean;
  showInvoiceHistoryAction?: boolean;
  showMemberBatchesAction?: boolean;
  showAssignMemberAction?: boolean;
  showIgnoreAction?: boolean;
  currentPage: number;
  totalPages: number;
  setCurrentPage: (page: number) => void;
  pageSize: number;
  setPageSize: (size: number) => void;
  isLoading: boolean;
  sortBy?: string;
  sortDescending?: boolean;
  onSort?: (sortKey: string) => void;
}

const ReconTransactionTable: React.FC<ReconTransactionTableProps> = ({
  data,
  handleViewMember,
  handleEditMember,
  handleCreateInvoice,
  handleViewInvoiceHistory,
  handleViewMemberBatches,
  handleSetIsUnallocated,
  handleSetComponentId,
  handleAssignMember,
  handleIgnoreTransaction,
  showConfirmComponentActions,
  showInvoiceHistoryAction,
  showMemberBatchesAction,
  showAssignMemberAction,
  showIgnoreAction,
  currentPage,
  totalPages,
  setCurrentPage,
  pageSize,
  setPageSize,
  isLoading,
  sortBy,
  sortDescending,
  onSort,
}) => {
  return (
    <Table
      columns={[
         {
          label: 'File',
          key: 'fileName',
          render: (row: ReconTransactionDTO) => (
            <span className="text-xs">{row.reconBatch?.fileRecord?.fileName || 'N/A'}</span>
          ),
        },
        {
          label: 'Batch',
          key: 'reconBatchId',
          render: (row: ReconTransactionDTO) => (
            <div className="flex flex-col">
              <span>
                {row.reconBatch?.dateStart
                  ? convertToUserTime(row.reconBatch.dateStart.toString())
                  : 'N/A'}
              </span>
              <span className="text-xs text-gray-500">
                {row.reconBatch?.dateEnd
                  ? convertToUserTime(row.reconBatch.dateEnd.toString())
                  : ''}
              </span>
            </div>
          ),
        },
        {
          label: 'Type',
          key: 'reconTypeId',
          sortable: true,
          sortKey: 'ReconTypeTitle',
          render: (row: ReconTransactionDTO) => (
            <span>{row.reconType?.title || 'Not Processed'}</span>
          ),
        },

        {
          label: 'Member',
          key: 'memberId',
          sortable: true,
          sortKey: 'FirstName',
          render: (row: ReconTransactionDTO) => (
            <div className="flex flex-col">
              <span>
                {row.member?.memberFirstName} {row.member?.memberLastName}
              </span>
              <span className="text-xs text-gray-500">{row.member?.idPassportNumber}</span>
            </div>
          ),
        },

        {
          label: 'Component',
          key: 'componentId',
          sortable: true,
          sortKey: 'ComponentTitle',
          render: (row: ReconTransactionDTO) => <span>{row.component?.title || 'N/A'}</span>,
        },

        {
          label: 'Transaction Date',
          key: 'transactionDate',
          sortable: true,
          sortKey: 'TransactionDate',
          format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
        },

        {
          label: 'Amount',
          key: 'amount',
          sortable: true,
          sortKey: 'Amount',
          render: (row: ReconTransactionDTO) => <span>R {row.amount?.toFixed(2)}</span>,
        },

        {
          label: 'Description',
          key: 'description',
        },

        {
          label: 'Paid?',
          key: 'isPaid',
          render: (row: ReconTransactionDTO) => renderBooleanBadge(row.isPaid ?? false, 'Paid','Unpaid'),
        },

        {
          label: 'Added',
          key: 'createdUtc',
          sortable: true,
          sortKey: 'CreatedUtc',
          format: (value: Date) => convertToUserTime(value?.toString() ?? ''),
        },
      ]}
      data={data}
      actions={{
        extra: createReconTransactionActions({
          onViewMember: handleViewMember,
          onEditMember: handleEditMember,
          onCreateInvoice: handleCreateInvoice,
          onViewInvoiceHistory: handleViewInvoiceHistory,
          onViewMemberBatches: handleViewMemberBatches,
          onSetIsUnallocated: handleSetIsUnallocated,
          onSetComponentId: handleSetComponentId,
          onAssignMember: handleAssignMember,
          onIgnoreTransaction: handleIgnoreTransaction,
          showConfirmComponentActions,
          showInvoiceHistoryAction,
          showMemberBatchesAction,
          showAssignMemberAction,
          showIgnoreAction,
        }),
      }}
      currentPage={currentPage}
      totalPages={totalPages}
      onPageChange={setCurrentPage}
      pageSize={pageSize}
      onPageSizeChange={setPageSize}
      isLoading={isLoading}
      sortBy={sortBy}
      sortDescending={sortDescending}
      onSort={onSort}
    />
  );
};

export default ReconTransactionTable;
